import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { RangeValue } from "@react-types/shared";
import {
  CalendarDate,
  getLocalTimeZone,
  today,
  toCalendarDate,
  ZonedDateTime,
  CalendarDateTime,
  getDayOfWeek,
} from "@internationalized/date";
import _ from "lodash";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import DateRangePicker from "../../components/DateRangePicker";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import currencyFormatter from "../../currency";
import { useToastMessageStore } from "../../store/toast-messages";
import { DbRead, DbWrite } from "../../database";
import { ExistingStiltPayment } from "../../models/stilt-payment";
import { logger } from "../../logging";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import { Link } from "react-router-dom";
import { BankDepositStatus } from "../../models/bank-deposit";
import BankDepositConfirmationDialog from "../../components/BankDepositConfirmationDialog";
import { ExistingGLAccount } from "../../models/gl-account";
import { useAuthStore } from "../../store/firebase-auth";
import { ExistingStiltInvoice } from "../../models/invoice";
import { ExistingCustomer } from "../../models/customer";
import { StyledTooltip } from "../../components/StyledTooltip";

interface Props {
  siteKey: string;
  quickbooksEnabled: boolean;
}

// Interface for display, including user name and confirmation status
interface DisplayAchPayment extends ExistingStiltPayment {
  createdByName: string;
  isConfirmed: boolean;
  isAlreadyDeposited: boolean;
  invoice: ExistingStiltInvoice | null;
  customer: ExistingCustomer | null;
}

// Function to get the default date range
const getDefaultDateRange = (): RangeValue<CalendarDate> => {
  const now = today(getLocalTimeZone());
  const currentDayOfWeek = getDayOfWeek(now, "en-US");
  if (currentDayOfWeek === 1) {
    return { start: now.subtract({ days: 3 }), end: now.subtract({ days: 1 }) };
  } else {
    return { start: now.subtract({ days: 1 }), end: now.subtract({ days: 1 }) };
  }
};

export default function AchReconciliationContainer({
  siteKey,
  quickbooksEnabled,
}: Props): JSX.Element {
  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const firebaseUser = useAuthStore((state) => state.firebaseUser);

  // State
  const [range, setRange] = useState<RangeValue<CalendarDate>>(
    getDefaultDateRange(),
  );
  const [stiltAchPayments, setStiltAchPayments] = useState<
    ExistingStiltPayment[]
  >([]);
  const [confirmedPaymentIds, setConfirmedPaymentIds] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [isCreatingDeposit, setIsCreatingDeposit] = useState(false);
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  const [glAccounts, setGlAccounts] = useState<ExistingGLAccount[]>([]);
  const [isLoadingGLAccounts, setIsLoadingGLAccounts] = useState(false);
  const [invoices, setInvoices] = useState<ExistingStiltInvoice[]>([]);
  const [customers, setCustomers] = useState<ExistingCustomer[]>([]);

  const userDisplayNamesMap = useUserDisplayNamesStore(
    (state) => state.userDisplayNames,
  );

  // Fetch GL accounts when component mounts
  useEffect(() => {
    const fetchGLAccounts = async () => {
      try {
        setIsLoadingGLAccounts(true);
        const unsubscribe = DbRead.gLAccounts.subscribeAll({
          siteKey,
          onChange: (accounts: ExistingGLAccount[]) => {
            setGlAccounts(accounts);
            setIsLoadingGLAccounts(false);
          },
          onError: (error) => {
            logger.error("Error fetching GL accounts:", error);
            addMessage({
              message: `Failed to load GL accounts: ${error instanceof Error ? error.message : "Unknown error"}. You may not be able to create a bank deposit.`,
              type: "error",
              id: "ach-recon-gl-accounts-error",
              dialog: false,
            });
            setIsLoadingGLAccounts(false);
          },
        });

        return () => {
          if (unsubscribe) {
            unsubscribe();
          }
        };
      } catch (error) {
        logger.error("Error setting up GL accounts subscription:", error);
        setIsLoadingGLAccounts(false);
      }
    };

    fetchGLAccounts();
  }, [siteKey, addMessage]);

  // --- Data Fetching ---
  const fetchData = useCallback(async () => {
    if (!range.start || !range.end) return;

    setLoading(true);
    setStiltAchPayments([]);
    setConfirmedPaymentIds(new Set());

    try {
      // Use getAllInDateRange
      const allPayments = await DbRead.payments.getAllInDateRange({
        siteKey,
        dateRange: range,
      });

      // Filter for ACH and EFT
      const filtered = allPayments.filter(
        (p) => p.paymentMethod === "ach" || p.paymentMethod === "eft",
      );
      setStiltAchPayments(filtered);
    } catch (error) {
      logger.error("Error fetching ACH/EFT payments:", error);
      addMessage({
        message: `Error fetching ACH/EFT payments: ${error instanceof Error ? error.message : "Unknown error"}`,
        type: "error",
        id: "ach-recon-fetch-error",
        dialog: false,
      });
    } finally {
      setLoading(false);
    }
  }, [siteKey, range, addMessage]);

  // Fetch invoices when stiltAchPayments changes
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const invoices: ExistingStiltInvoice[] = [];
        const invoicePromises = stiltAchPayments.map(async (payment) => {
          if (payment.invoiceID) {
            const invoice = await DbRead.invoices.getSingle(
              siteKey,
              payment.invoiceID,
            );
            if (invoice) {
              invoices.push(invoice);
            }
          }
        });
        await Promise.all(invoicePromises);
        setInvoices(invoices);
      } catch (error) {
        logger.error("Error fetching invoices:", error);
        addMessage({
          message: `Error fetching invoice details: ${error instanceof Error ? error.message : "Unknown error"}. Some data may be missing.`,
          type: "warning",
          id: "ach-recon-invoice-error",
          dialog: false,
        });
      }
    };

    fetchInvoices();
  }, [siteKey, stiltAchPayments, addMessage]);

  // Fetch customers when stiltAchPayments changes
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customers: ExistingCustomer[] = [];
        const customerPromises = stiltAchPayments.map(async (payment) => {
          if (payment.customerID) {
            const customer = await DbRead.customers.get(
              siteKey,
              payment.customerID,
            );
            if (customer) {
              customers.push(customer);
            }
          }
        });
        await Promise.all(customerPromises);
        setCustomers(customers);
      } catch (error) {
        logger.error("Error fetching customers:", error);
        addMessage({
          message: `Error fetching customer details: ${error instanceof Error ? error.message : "Unknown error"}. Some data may be missing.`,
          type: "warning",
          id: "ach-recon-customer-error",
          dialog: false,
        });
      }
    };

    fetchCustomers();
  }, [siteKey, stiltAchPayments, addMessage]);

  // --- Event Handlers ---
  const handleDateRangeChange = (
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ) => {
    logger.debug(`ACH Recon: Invoked handleDateRangeChange`);
    try {
      if (value.start && value.end) {
        const convertedStart = toCalendarDate(value.start);
        const convertedEnd = toCalendarDate(value.end);
        setRange({ start: convertedStart, end: convertedEnd });
        // Reset data when range changes
        setStiltAchPayments([]);
        setConfirmedPaymentIds(new Set());
      }
    } catch (e) {
      logger.error("Error in handleDateRangeChange", e);
    }
  };

  const handleToggleConfirm = (paymentId: string) => {
    // Find the payment to check if it's already deposited
    const payment = stiltAchPayments.find((p) => p.id === paymentId);

    // If payment already has a bankDepositID, don't allow toggle
    if (payment && payment.bankDepositID !== undefined) {
      addMessage({
        message:
          "This payment is already part of a bank deposit and cannot be selected again.",
        type: "warning",
        dialog: false,
        id: "ach-recon-already-deposited",
      });
      return;
    }

    setConfirmedPaymentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(paymentId)) {
        newSet.delete(paymentId);
      } else {
        newSet.add(paymentId);
      }
      return newSet;
    });
  };

  // Function to handle Select All checkbox
  const handleSelectAll = () => {
    // Get all payments that are not already deposited
    const availablePaymentIds = displayPayments
      .filter((p) => !p.isAlreadyDeposited)
      .map((p) => p.id);

    // If all available payments are already confirmed, unselect all
    // Otherwise, select all available payments
    if (
      availablePaymentIds.length === confirmedPaymentIds.size &&
      availablePaymentIds.every((id) => confirmedPaymentIds.has(id))
    ) {
      setConfirmedPaymentIds(new Set());
    } else {
      setConfirmedPaymentIds(new Set(availablePaymentIds));
    }
  };

  // Function to open the deposit dialog
  const handleOpenDepositDialog = () => {
    if (confirmedPaymentIds.size === 0) {
      addMessage({
        message: "No ACH/EFT payments have been confirmed for deposit",
        type: "error",
        dialog: false,
        id: "ach-recon-no-confirmed",
      });
      return;
    }

    // Check if any confirmed payments aren't synced to QuickBooks when QuickBooks is enabled
    if (quickbooksEnabled) {
      const unsyncedPayments = displayPayments.filter(
        (payment) =>
          confirmedPaymentIds.has(payment.id) &&
          !payment.customData?.accountingRefID,
      );

      if (unsyncedPayments.length > 0) {
        addMessage({
          message: `Cannot create bank deposit: ${unsyncedPayments.length} payment(s) are not synced to QuickBooks. Please sync these payments first.`,
          type: "error",
          dialog: false,
          id: "ach-recon-unsynced-payments",
        });
        return;
      }
    }

    setShowDepositDialog(true);
  };

  // Function to create a bank deposit from confirmed ACH payments
  const handleCreateBankDeposit = async (
    selectedGLAccount: ExistingGLAccount,
    depositDate: string,
    memo: string,
    depositAmount: number,
  ) => {
    try {
      setIsCreatingDeposit(true);

      // Get confirmed payment IDs
      const matchedPaymentIds = [...confirmedPaymentIds];
      const confirmedPayments = stiltAchPayments.filter((p) =>
        confirmedPaymentIds.has(p.id),
      );

      // Prepare bank deposit request data
      const bankDepositData = {
        siteKey: siteKey,
        glAccountID: selectedGLAccount.id,
        totalAmount: depositAmount,
        paymentIDs: matchedPaymentIds,
        memo:
          memo ||
          `ACH/EFT Deposit - ${DateTime.fromISO(depositDate).toFormat("MM/dd/yyyy")}`,
        timestampDepositedISO: depositDate,
        status: "deposited" as BankDepositStatus,
        customData: {},
        createdBy: firebaseUser?.uid || "",
      };

      await DbWrite.bankDeposits.create({ bankDeposit: bankDepositData });

      // Show success message
      addMessage({
        message: `Bank deposit created successfully for ${matchedPaymentIds.length} ACH/EFT payments totaling ${currencyFormatter(depositAmount, "USD")}`,
        type: "success",
        dialog: false,
        id: "ach-recon-deposit-created",
      });

      // Reset confirmed payments after creating deposit
      setConfirmedPaymentIds(new Set());

      // Refresh the data
      await fetchData();
    } catch (error) {
      console.log(
        "Error creating bank deposit:",
        JSON.stringify(error, null, 2),
      );
      addMessage({
        message: `Error creating bank deposit: ${error instanceof Error ? error.message : "Unknown error"}`,
        type: "error",
        dialog: false,
        id: "ach-recon-deposit-error",
      });
    } finally {
      setIsCreatingDeposit(false);
      setShowDepositDialog(false);
    }
  };

  // --- Derived Data & Calculations ---
  const displayPayments = useMemo((): DisplayAchPayment[] => {
    const enriched = stiltAchPayments.map((p) => {
      const uid = p.createdBy;
      const name = uid ? userDisplayNamesMap[uid] || uid : "Unknown User";
      // Check if payment already has a bankDepositID (already in a bank deposit)
      const isAlreadyDeposited = p.bankDepositID !== undefined;
      // Find related invoice and customer
      const invoice = invoices.find((i) => i.id === p.invoiceID) ?? null;
      const customer = customers.find((c) => c.id === p.customerID) ?? null;

      return {
        ...p,
        createdByName: name,
        isConfirmed: confirmedPaymentIds.has(p.id),
        isAlreadyDeposited,
        invoice,
        customer,
      };
    });

    // Sort by Timestamp Payment Made
    return _.orderBy(
      enriched,
      [(p) => p.timestampPaymentMade.seconds],
      ["asc"],
    );
  }, [
    stiltAchPayments,
    userDisplayNamesMap,
    invoices,
    customers,
    confirmedPaymentIds,
  ]);

  // Calculate if all available filtered payments are selected
  const allAvailableSelected = useMemo(() => {
    const availablePayments = displayPayments.filter(
      (p) => !p.isAlreadyDeposited,
    );
    return (
      availablePayments.length > 0 &&
      availablePayments.every((p) => confirmedPaymentIds.has(p.id))
    );
  }, [displayPayments, confirmedPaymentIds]);

  const totalStiltAmount = useMemo(
    () => _.sumBy(stiltAchPayments, "amount"),
    [stiltAchPayments],
  );

  // Calculate total based on filtered data
  const totalAvailableAmount = useMemo(
    () =>
      _.sumBy(
        displayPayments.filter((p) => p.bankDepositID === undefined),
        "amount",
      ),
    [displayPayments],
  );

  const totalConfirmedAmount = useMemo(
    () =>
      _.sumBy(
        displayPayments.filter((p) => confirmedPaymentIds.has(p.id)),
        "amount",
      ),
    [displayPayments, confirmedPaymentIds],
  );

  // Calculate unconfirmed amount (from available payments only)
  const totalUnconfirmedAmount = useMemo(
    () =>
      _.sumBy(
        displayPayments.filter(
          (p) =>
            p.bankDepositID === undefined && !confirmedPaymentIds.has(p.id),
        ),
        "amount",
      ),
    [displayPayments, confirmedPaymentIds],
  );

  // Calculate total and count for confirmed payments
  const confirmedPaymentsTotal = useMemo(
    () =>
      _.sumBy(
        displayPayments.filter((p) => confirmedPaymentIds.has(p.id)),
        "amount",
      ),
    [displayPayments, confirmedPaymentIds],
  );

  const confirmedPaymentsCount = useMemo(
    () => displayPayments.filter((p) => confirmedPaymentIds.has(p.id)).length,
    [displayPayments, confirmedPaymentIds],
  );

  return (
    <div className="space-y-6">
      {/* Top Controls: Date Range and Fetch Button */}
      <div className="rounded-lg bg-white p-4 shadow-md">
        <div className="flex flex-wrap gap-4">
          <div className="min-w-[240px] flex-1">
            <DateRangePicker
              value={range as any}
              onChange={handleDateRangeChange}
            />
          </div>

          <div className="flex gap-2">
            <BaseButtonPrimary
              onClick={fetchData}
              disabled={loading}
              className="h-10"
              isBusy={loading}
            >
              {loading ? "Loading..." : "Fetch ACH/EFT Data"}
            </BaseButtonPrimary>

            <BaseButtonSecondary
              onClick={handleOpenDepositDialog}
              disabled={
                confirmedPaymentIds.size === 0 || loading || isCreatingDeposit
              }
              className="h-10"
              isBusy={isCreatingDeposit}
            >
              {isCreatingDeposit ? "Creating..." : "Create Bank Deposit"}
            </BaseButtonSecondary>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-10">
          <LoadingClipboardAnimation />
        </div>
      )}

      {/* Results Area */}
      {!loading && stiltAchPayments.length > 0 && (
        <div className="space-y-4">
          {/* Summary Totals */}
          <div
            className={`rounded-lg border border-gray-200 bg-gray-50 p-4 shadow`}
          >
            <h3 className="mb-2 text-lg font-medium">ACH/EFT Summary</h3>

            <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
              <div>
                <span className="font-medium">Total Available:</span>{" "}
                {currencyFormatter(totalAvailableAmount, "USD")} (
                {
                  displayPayments.filter((p) => p.bankDepositID === undefined)
                    .length
                }{" "}
                payments)
              </div>
              <div className="text-green-700">
                <span className="font-medium">Total Confirmed:</span>{" "}
                {currencyFormatter(totalConfirmedAmount, "USD")} (
                {confirmedPaymentsCount} payments)
              </div>
              <div
                className={`${totalUnconfirmedAmount > 0.01 ? "text-orange-700" : "text-gray-700"}`}
              >
                <span className="font-medium">Total Unconfirmed:</span>{" "}
                {currencyFormatter(totalUnconfirmedAmount, "USD")}
              </div>
            </div>
          </div>

          {/* ACH/EFT Payments Table */}
          <div className="overflow-hidden rounded-lg bg-white shadow-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-14 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={allAvailableSelected}
                        onChange={handleSelectAll}
                        title="Select All Available Payments"
                      />
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Date/Time
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Method
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Amount
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Invoice #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Customer
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Entered By
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Memo
                  </th>
                  {quickbooksEnabled && (
                    <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      QB Sync
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {displayPayments.map((payment) => (
                  <tr
                    key={payment.id}
                    className={`${payment.isConfirmed ? "bg-green-50" : ""} ${payment.isAlreadyDeposited ? "bg-gray-100" : ""}`}
                  >
                    <td className="whitespace-nowrap px-3 py-1">
                      {payment.isAlreadyDeposited ? (
                        <div
                          className="cursor-not-allowed rounded-full text-green-500"
                          title="Already part of a bank deposit"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={payment.isConfirmed}
                            onChange={() => handleToggleConfirm(payment.id)}
                            id={`payment-${payment.id}`}
                          />
                          <label
                            htmlFor={`payment-${payment.id}`}
                            className="sr-only"
                          >
                            {payment.isConfirmed
                              ? "Unselect payment"
                              : "Select payment"}
                          </label>
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm text-gray-500">
                      {DateTime.fromSeconds(
                        payment.timestampPaymentMade.seconds,
                      ).toFormat("ff")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm uppercase text-gray-500">
                      {payment.paymentMethod}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm text-gray-500">
                      {currencyFormatter(payment.amount, "USD")}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm">
                      {payment.invoice ? payment.invoice.invoiceNumber : "N/A"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm">
                      {payment.customer ? (
                        <Link
                          to={`/customers/${payment.customer.id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {payment.customer.name}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1 text-sm font-medium text-gray-800">
                      {payment.createdByName}
                    </td>
                    <td className="px-3 py-1 text-sm text-gray-500">
                      {payment.memo || "-"}
                    </td>
                    {quickbooksEnabled && (
                      <td className="whitespace-nowrap px-3 py-1 text-center">
                        {payment.customData?.accountingRefID ? (
                          <CheckCircleIcon
                            className="mx-auto h-5 w-5 text-green-500"
                            title="Synced with QuickBooks"
                          />
                        ) : (
                          <div className="flex justify-center">
                            <StyledTooltip
                              title={
                                payment.accountingSync?.statusMessage ??
                                "Not synced with QuickBooks"
                              }
                            >
                              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                            </StyledTooltip>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Message when no data is fetched or found */}
      {!loading && stiltAchPayments.length === 0 && (
        <div className="rounded-lg bg-white py-10 text-center text-gray-500 shadow-md">
          Click "Fetch ACH/EFT Data" to load payments, or none were found for
          the selected date range.
        </div>
      )}

      {/* Bank Deposit Confirmation Dialog */}
      <BankDepositConfirmationDialog
        isOpen={showDepositDialog}
        quickbooksEnabled={quickbooksEnabled}
        onClose={() => setShowDepositDialog(false)}
        onConfirm={handleCreateBankDeposit}
        totalAmount={confirmedPaymentsTotal}
        paymentCount={confirmedPaymentsCount}
        depositType="ach"
        isLoading={isCreatingDeposit}
        glAccounts={glAccounts}
        siteKey={siteKey}
        firebaseUser={firebaseUser!}
        currency="USD"
      />
    </div>
  );
}
